import React, { useState, useEffect } from "react";
import { useIntl } from "umi";
import classNames from "classnames";
import TimeCompare from "@/pages/datalab/components/DateComponents/TimeCompare";
import BaseTimePicker from "@/pages/datalab/components/DateComponents/BaseTimePicker";
import type { TDateRange, TRelativeTime, TDateGranularity } from '@dbl/core/date';
import { IQuickValue } from "@/pages/datalab/components/DateComponents/DateEventShortcut";
import { LANGUAGE_EN } from "@home/_config/config";
import './index.scss';

/**
 * 时间值接口（扩展 BaseTimePicker 的 ITimeValue，添加 compareTimes）
 */
interface ITimeAndCompareValue {
  dateRange: string[];
  dateType: TDateGranularity;
  quickValue: IQuickValue;
  compareTimes?: TRelativeTime[];
}

/**
 * BaseTimeAndCompareRange 组件 Props 接口
 */
interface IBaseTimeAndCompareRangeProps {
  value: ITimeAndCompareValue;
  onConfirm?: (value: ITimeAndCompareValue) => void;
  onCancel?: () => void;
  className?: string;
  mountClassNames?: string;
  updatePosition?: () => void;
}

/**
 * 主要用于 静态日期
 * @param props
 * @constructor
 */
const BaseTimeAndCompareRange = (props: IBaseTimeAndCompareRangeProps) => {
  const { value, onConfirm, onCancel, className, mountClassNames, updatePosition } = props;
  const { locale , formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN; // 是否是英文
  const [timeValue, setTimeValue] = useState<ITimeAndCompareValue>(value);
  // 使用 TimeCompare 组件的 compareTimes 状态
  const [compareTimes, setCompareTimes] = useState<TRelativeTime[]>(value?.compareTimes || []);
  
  // 当外部传入的 value.compareTimes 变化时，同步更新 compareTimes 状态
  useEffect(() => {
    setCompareTimes(value?.compareTimes || []);
  }, [value?.compareTimes]);
  
  // btcr 是 BaseTimeAndCompareRange缩写
  return (<div className={classNames('dbl-btcr-wrap', className)}>
    <div className={'dbl-btcr-wrap-base-time'}>
      <span className={classNames('dbl-btcr-wrap-base-time-title' , { 'dbl-btcr-wrap-base-time-title-en': isEn })}>{formatMessage({id: 'dbl_time_compare_base'})}</span>
      <BaseTimePicker
        value={timeValue}
        onChange={({dateRange, dateType, quickValue}) => {
          setTimeValue((timeValue) => ({
            ...timeValue,
            dateRange,
            dateType,
            compareTimes: dateType !== timeValue.dateType ? [] : timeValue.compareTimes,
            quickValue,
          }));
        }}
        mountClassNames={mountClassNames}
        pickerLabelClassName={classNames({
          'dbl-base-time-picker-label-one': compareTimes.length === 1,
          'dbl-base-time-picker-label-more': compareTimes.length > 1,
        })}
      />
    </div>
    <TimeCompare
      showBaseTime={false}
      baseTimeRange={timeValue.dateRange as unknown as TDateRange}
      timeGranularity={timeValue.dateType}
      defaultCompareTimes={timeValue?.compareTimes ||[]}
      onCancel={() => {
        onCancel?.();
      }}
      onConfirm={(compares) => {
        onConfirm?.({
          ...timeValue,
          compareTimes: compares,
        });
      }}
      mountClassNames={mountClassNames}
      onStateChange={(isOpen, compares) => {
        // 更新 TimeCompare 组件的 compareTimes 状态
        setCompareTimes(compares);
        updatePosition?.();
      }}
    />
  </div>);
};

export default BaseTimeAndCompareRange;